import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ObservationsService,
  ObservationsPaginatedResult
} from '../../../infrastructure/services/observations/services/observations.service';
import { ObservationsLaboratoryService } from '../../../infrastructure/services/observations/services/observations-laboratory.service';
import {
  ObservationsQueryParams,
  ReprocessHistoryDTO,
  ObservationMetricsParams
} from '../../../infrastructure/services/observations/api/observations-api.interface';
import { MASTER_DATA_PORT } from '@service/preclinical/master-data.port';
import { CatalogRepository } from '@service/catalog/catalog.repository';
import { FilterOptionsResponse } from '@model/filter/filter-options.model';
import { ObservationMetrics } from '@model/observation/observation-metrics.model';

export type ObservationModuleType = 'preclinical' | 'laboratory';

export interface StatusOption {
  id: string;
  name: string;
}

/**
 * Interface for observation service operations.
 * Both ObservationsService and ObservationsLaboratoryService implement these methods.
 */
interface ObservationServiceOperations {
  getObservations(
    params?: ObservationsQueryParams
  ): Observable<ObservationsPaginatedResult>;
  updateObservationStatus(
    observationId: string,
    statusId: string
  ): Observable<void>;
  getObservationHistory(
    observationId: string
  ): Observable<ReprocessHistoryDTO[]>;
  addObservationHistory(
    observationId: string,
    observationText: string,
    lgCode?: string
  ): Observable<void>;
  exportObservations(params?: ObservationsQueryParams): Observable<Blob>;
  getMetrics(
    params?: ObservationMetricsParams
  ): Observable<ObservationMetrics | null>;
  getMetricsStream(
    params?: ObservationMetricsParams,
    intervalMs?: number
  ): Observable<ObservationMetrics | null>;
}

@Injectable({
  providedIn: 'root'
})
export class ObservationsPresenter {
  private readonly observationsService = inject(ObservationsService);
  private readonly observationsLaboratoryService = inject(
    ObservationsLaboratoryService
  );
  private readonly masterDataPort = inject(MASTER_DATA_PORT);
  private readonly catalogRepository = inject(CatalogRepository);

  private currentModule: ObservationModuleType = 'preclinical';

  /**
   * Map of module type to its corresponding service.
   * This pattern avoids repetitive if/else checks throughout the class.
   */
  private get serviceMap(): Record<
    ObservationModuleType,
    ObservationServiceOperations
  > {
    return {
      preclinical: this.observationsService,
      laboratory: this.observationsLaboratoryService
    };
  }

  /**
   * Get the current service based on active module.
   */
  private get currentService(): ObservationServiceOperations {
    return this.serviceMap[this.currentModule];
  }

  /**
   * Validates if a value is a valid module type using the serviceMap as source of truth.
   * No string comparisons needed - just checks if key exists in the map.
   */
  isValidModule(value: string | null): value is ObservationModuleType {
    return value !== null && value in this.serviceMap;
  }

  setModule(module: ObservationModuleType): void {
    this.currentModule = module;
  }

  getModule(): ObservationModuleType {
    return this.currentModule;
  }

  getObservations(
    params?: ObservationsQueryParams
  ): Observable<ObservationsPaginatedResult> {
    return this.currentService.getObservations(params);
  }

  updateObservationStatus(
    observationId: string,
    statusId: string
  ): Observable<void> {
    return this.currentService.updateObservationStatus(observationId, statusId);
  }

  getObservationHistory(
    observationId: string
  ): Observable<ReprocessHistoryDTO[]> {
    return this.currentService.getObservationHistory(observationId);
  }

  addObservationHistory(
    observationId: string,
    observationText: string,
    lgCode?: string
  ): Observable<void> {
    return this.currentService.addObservationHistory(
      observationId,
      observationText,
      lgCode
    );
  }

  /**
   * Load reprocess statuses based on current module.
   * Laboratory uses CatalogRepository, Preanalitica uses MasterDataPort.
   */
  loadReprocessStatuses(): Observable<StatusOption[]> {
    if (this.currentModule === 'laboratory') {
      return this.catalogRepository.getReprocessStatuses().pipe(
        map((statuses) =>
          statuses.map((s) => ({
            id: s.id,
            name: s.description
          }))
        )
      );
    }
    return this.masterDataPort.getReprocessStatuses();
  }

  /**
   * Export observations to CSV based on current module.
   */
  exportObservations(params?: ObservationsQueryParams): Observable<Blob> {
    return this.currentService.exportObservations(params);
  }

  /**
   * Get filter options for observations.
   * Devuelve: reprocessCauses + sites.
   * Laboratorio: Usa getAllCauses() para traer todas las causas de la maestra.
   * Preanalítica: Usa MASTER_DATA_PORT.getReprocessCauses().
   */
  getFilterOptions(): Observable<FilterOptionsResponse> {
    if (this.currentModule === 'laboratory') {
      // Laboratorio: traer todas las causas de la maestra sin filtrar por contexto
      return forkJoin({
        allCauses: this.catalogRepository.getAllCauses(),
        statuses: this.catalogRepository.getReprocessStatuses(),
        sites: this.masterDataPort.getSites()
      }).pipe(
        map(({ allCauses, statuses, sites }) => {
          const filteredCauses = allCauses.filter(
            (c) => c.description && c.description !== '(Vacío)'
          );
          return {
            regions: [],
            sites,
            examTypes: [],
            reprocessCauses: filteredCauses.map((c) => ({
              id: c.id,
              name: c.description
            })),
            sampleStatuses: statuses.map((s) => ({
              code: s.id,
              name: s.description
            }))
          };
        })
      );
    }
    return forkJoin({
      causes: this.masterDataPort.getReprocessCauses(),
      statuses: this.masterDataPort.getReprocessStatuses(),
      sites: this.masterDataPort.getSites()
    }).pipe(
      map(({ causes, statuses, sites }) => ({
        regions: [],
        sites,
        examTypes: [],
        reprocessCauses: causes.map((c) => ({ id: c.id, name: c.name })),
        sampleStatuses: statuses.map((s) => ({
          code: s.id,
          name: s.name
        }))
      }))
    );
  }

  /**
   * Get observation metrics.
   * Available for both Preanalítica and Laboratory modules.
   */
  getMetrics(
    params?: ObservationMetricsParams
  ): Observable<ObservationMetrics | null> {
    return this.currentService.getMetrics(params);
  }

  /**
   * Auto-refreshing stream of observation metrics.
   * Available for both Preanalítica and Laboratory modules.
   */
  getMetricsStream(
    params?: ObservationMetricsParams,
    intervalMs = 60000
  ): Observable<ObservationMetrics | null> {
    return this.currentService.getMetricsStream(params, intervalMs);
  }
}
